// AT-2 (2026-06-04) — Aba "Atestado" dentro do contrato.
// Lista atestados existentes do contrato + botão "Iniciar atestado" com checklist
// de pré-requisitos visual. Click num atestado abre ModalAtestado (editor).

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Card, Typography, Button, Table, Tag, Empty, Spin, Space,
  Modal, Checkbox, Alert, Flex, message, Tooltip, Popconfirm,
} from 'antd';
import {
  PlusOutlined, FileDoneOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ExclamationCircleOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { usePerms } from '../../hooks/usePerms';
import { API_URL } from '../../config';
import { getToken } from './utils';
import { fmtDataBr } from '../../utils/dataFmt';
import ModalAtestado from './ModalAtestado';
// AT-7 (2026-06-09): workflow novo compartilhado (backlog → … → timbrado_cat)
import { statusCfg } from './atestadoStatus';

const { Text, Title } = Typography;

interface AtestadoRow {
  id: string;
  numero: string | null;
  numero_seq: number | null;
  status: string;
  data_emissao: string | null;
  criado_em: string;
  atualizado_em: string;
  criado_por: string | null;
}

interface Contrato {
  id: string;
  numero?: string;
  titulo?: string;
  permite_subcontratacao?: boolean;
  atestado_contrato_assinado?: boolean;
  arts_total?: number;
}

interface Props {
  contrato: Contrato;
  onAtualizar: () => void;
}


export function AbaAtestadosInner({ contrato, onAtualizar }: Props) {
  const { pode } = usePerms();
  const podeCriar = pode('operacional.atestado.criar');
  const [lista, setLista] = useState<AtestadoRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalIniciar, setModalIniciar] = useState(false);
  const [subcontratado, setSubcontratado] = useState(false);
  const [iniciando, setIniciando] = useState(false);
  const [atestadoAberto, setAtestadoAberto] = useState<string | null>(null);
  // ATM-T1 fix (2026-06-07): conta PDFs do contrato no Drive pra pre-req
  const [pdfsContratoTotal, setPdfsContratoTotal] = useState<number>(0);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_listar', contrato_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setLista(Array.isArray(d) ? d : []);
    } catch (e: any) {
      message.error('Falha ao carregar atestados: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  async function carregarPdfsContrato() {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_listar_arvore', contrato_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const pdfs = (d.files || []).filter((f: any) =>
        /\.pdf$/i.test(f.s3_key || '') &&
        /contrato|minuta/i.test(f.nome || '')
      );
      setPdfsContratoTotal(pdfs.length);
    } catch (_) { setPdfsContratoTotal(0); }
  }
  useEffect(() => { carregar(); carregarPdfsContrato(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [contrato.id]);

  // Pré-requisitos pra iniciar
  const reqs = useMemo(() => [
    {
      label: 'Cliente assinou o contrato (marcado em Visão geral)',
      ok: !!contrato.atestado_contrato_assinado,
      hint: 'Em Visão geral → marque "Atestado" → "Contrato assinado"',
    },
    {
      label: 'PDF do contrato no Drive (nome com "contrato" ou "minuta")',
      ok: pdfsContratoTotal > 0,
      hint: 'Vá pra aba Drive e suba o PDF do contrato com a palavra "contrato" no nome',
    },
    {
      label: 'Pelo menos uma ART preenchida',
      ok: (contrato.arts_total || 0) > 0,
      hint: 'Vá pra aba ARTs e cadastre',
    },
    ...(subcontratado ? [{
      label: 'Contrato permite subcontratação',
      ok: !!contrato.permite_subcontratacao,
      hint: 'Em Editar dados → marque "Permite subcontratação"',
    }] : []),
  ], [contrato, subcontratado, pdfsContratoTotal]);

  const todosOk = reqs.every(r => r.ok);

  async function iniciar(forcar = false) {
    setIniciando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_iniciar', contrato_id: contrato.id, subcontratado, forcar },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(`Atestado ${d.numero || ''} criado em rascunho`);
      setModalIniciar(false);
      await carregar();
      onAtualizar();
      // Abre direto pra edição
      if (d.id) setAtestadoAberto(d.id);
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message;
      // 412 = pré-req faltando — pergunta se quer forçar
      if (e.response?.status === 412) {
        Modal.confirm({
          title: 'Pré-requisitos não cumpridos',
          content: <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{msg}</pre>,
          okText: 'Forçar criação mesmo assim',
          cancelText: 'Cancelar',
          okButtonProps: { danger: true },
          onOk: () => iniciar(true),
        });
      } else {
        message.error('Falha ao iniciar: ' + msg);
      }
    } finally { setIniciando(false); }
  }

  const colunas = [
    {
      title: 'Número', dataIndex: 'numero', width: 160,
      render: (v: string | null) => v ? <Text strong style={{ color: '#020D7F' }}>{v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 200,
      render: (s: string) => {
        const c = statusCfg(s);
        return <Tag color={c.tagColor}>{c.label}</Tag>;
      },
    },
    {
      title: 'Data emissão', dataIndex: 'data_emissao', width: 110,
      render: (v: string | null) => v ? fmtDataBr(v) : <Text type="secondary" italic style={{ fontSize: 11 }}>—</Text>,
    },
    {
      title: 'Atualizado', dataIndex: 'atualizado_em', width: 120,
      render: (v: string) => v ? <Text style={{ fontSize: 12 }}>{fmtDataBr(v)}</Text> : null,
    },
    {
      title: 'Criado por', dataIndex: 'criado_por', ellipsis: true,
      render: (v: string | null) => v ? <Text style={{ fontSize: 11 }}>{v.split('@')[0]}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: '', width: 60, dataIndex: 'id',
      render: (id: string, row: AtestadoRow) => ['backlog', 'draft'].includes(row.status) ? (
        <Popconfirm title="Excluir atestado?" description={`O atestado ${row.numero || 'sem número'} será apagado.`}
          okText="Excluir" cancelText="Cancelar" okButtonProps={{ danger: true }}
          onConfirm={async (e) => {
            e?.stopPropagation();
            try {
              const token = await getToken();
              await axios.post(`${API_URL}/comercial/propostas-geradas`,
                { action: 'op_atestado_excluir', id },
                { headers: { Authorization: token } });
              message.success('Excluído');
              await carregar();
            } catch (err: any) {
              message.error('Falha: ' + (err.response?.data?.error || err.message));
            }
          }}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()} />
        </Popconfirm>
      ) : null,
    },
  ];

  return (
    <div style={{ padding: 12 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0, color: '#020D7F' }}>
          <FileDoneOutlined /> Atestados Técnicos {lista.length > 0 && <Tag>{lista.length}</Tag>}
        </Title>
        {podeCriar && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalIniciar(true)}>
            Iniciar atestado
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" style={{ padding: 40 }}><Spin /></Flex>
      ) : lista.length === 0 ? (
        <Empty
          description={
            <Space direction="vertical" size={4}>
              <Text>Sem atestado iniciado pra esse contrato</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {podeCriar
                  ? 'Clique em "Iniciar atestado" pra começar.'
                  : 'Você não tem permissão pra criar (operacional.atestado.criar).'}
              </Text>
            </Space>
          } />
      ) : (
        <Table size="small" rowKey="id" dataSource={lista} columns={colunas}
          pagination={false}
          onRow={(row) => ({
            style: { cursor: 'pointer' },
            onClick: () => setAtestadoAberto(row.id),
          })} />
      )}

      {/* Modal "Iniciar atestado" — checklist + checkbox subcontratado */}
      <Modal open={modalIniciar} onCancel={() => !iniciando && setModalIniciar(false)}
        title="Iniciar atestado técnico"
        confirmLoading={iniciando}
        okText="Criar rascunho" cancelText="Cancelar"
        okButtonProps={{ disabled: !todosOk }}
        onOk={() => iniciar(false)} width={520}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert type="info" showIcon
            message="Vamos verificar os pré-requisitos"
            description="Marque abaixo se este contrato envolveu subcontratação. O sistema vai validar antes de criar o rascunho." />

          <div style={{ background: '#fafafa', padding: 10, borderRadius: 4 }}>
            <Checkbox checked={subcontratado} onChange={e => setSubcontratado(e.target.checked)}>
              <Text strong style={{ fontSize: 12 }}>Este contrato foi executado com subcontratação</Text>
            </Checkbox>
          </div>

          <div>
            <Text strong style={{ fontSize: 12, color: '#888' }}>Pré-requisitos</Text>
            {reqs.map((r, i) => (
              <div key={i} style={{ marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                {r.ok
                  ? <CheckCircleOutlined style={{ color: '#0a7f4a', fontSize: 16, marginTop: 2 }} />
                  : <CloseCircleOutlined style={{ color: '#B91626', fontSize: 16, marginTop: 2 }} />}
                <div>
                  <Text style={{ fontSize: 12, color: r.ok ? '#222' : '#B91626' }}>{r.label}</Text>
                  {!r.ok && <div style={{ fontSize: 10, color: '#888' }}>{r.hint}</div>}
                </div>
              </div>
            ))}
          </div>

          {!todosOk && (
            <Alert type="warning" showIcon icon={<ExclamationCircleOutlined />}
              message="Faltam pré-requisitos"
              description={<Text style={{ fontSize: 11 }}>
                Você pode resolver agora ou forçar a criação — vai dar a opção depois.
              </Text>} />
          )}
        </Space>
      </Modal>

      {/* Editor do atestado — modal full-width */}
      {atestadoAberto && (
        <ModalAtestado
          atestadoId={atestadoAberto}
          contrato={contrato}
          onClose={() => setAtestadoAberto(null)}
          onSaved={async () => { await carregar(); onAtualizar(); }}
        />
      )}
    </div>
  );
}

export function AbaAtestados(props: Props) {
  return <AntApp><AbaAtestadosInner {...props} /></AntApp>;
}
